import { CopyOutlined, DeleteOutlined, EditOutlined, MoreOutlined } from "@ant-design/icons"
import { Button, Dropdown, Flex, Tooltip } from "antd"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { cn } from "../../../../../../../../utils/cn.ts"
import { type ProjectTypeDetail } from "../../../../../../types/project.types.ts"
import {
  PROJECT_ENTITY_CATEGORIES,
  PROJECT_ENTITY_CATEGORIES_MAP
} from "../../../../../constants/project-entity-category.ts"
import { typeLabel } from "../../../../../../helpers/repeated-project-type.ts"

interface ProjectViewMilestonesHeaderItemProps {
  item: ProjectTypeDetail
  isLocked: boolean
  deleteAllowed?: boolean
  editAllowed?: boolean
  saveAsTemplateAllowed?: boolean
  onEdit: (item: ProjectTypeDetail) => void
  onDelete: (item: ProjectTypeDetail) => void
  onSaveAsTemplate: (item: ProjectTypeDetail) => void
}

export function ProjectViewMilestonesHeaderItem({
  item,
  isLocked,
  deleteAllowed,
  editAllowed,
  saveAsTemplateAllowed,
  onEdit,
  onDelete,
  onSaveAsTemplate
}: ProjectViewMilestonesHeaderItemProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const changeReqStyle =
    PROJECT_ENTITY_CATEGORIES_MAP?.[PROJECT_ENTITY_CATEGORIES?.CHANGE_REQUEST]
      .style

  const handleMenuClick = ({ key }: { key: string }) => {
    if (key === "edit") onEdit(item)
    if (key === "save-as-template") onSaveAsTemplate(item)
    if (key === "delete") onDelete(item)
  }

  return (
    <Flex align="center" className="group/tab min-h-6 px-4 py-2" gap={6}>
      {item ? typeLabel(item) : null}
      {item?.category === PROJECT_ENTITY_CATEGORIES.CHANGE_REQUEST && (
        <Tooltip title="Change Request">
          <span
            style={{ backgroundColor: changeReqStyle?.color }}
            className="absolute top-1 right-1 size-2 rounded-full "
          />
        </Tooltip>
      )}
      {!isLocked && (
        <div
          className={cn(
            "max-w-0 overflow-hidden group-hover/tab:max-w-[32px] transition-all duration-200 ease-in-out",
            { "max-w-[32px]": open }
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {(deleteAllowed || editAllowed || saveAsTemplateAllowed) && (
            <Dropdown
              open={open}
              onOpenChange={setOpen}
              menu={{
                items: [
                  ...(editAllowed
                    ? [
                        {
                          key: "edit",
                          icon: <EditOutlined />,
                          label: t("projectTypeMenu.edit")
                        }
                      ]
                    : []),
                  ...(saveAsTemplateAllowed
                    ? [
                        {
                          key: "save-as-template",
                          icon: <CopyOutlined />,
                          label: t("projectTypeMenu.saveAsTemplate")
                        }
                      ]
                    : []),
                  ...(deleteAllowed
                    ? [
                        {
                          key: "delete",
                          icon: <DeleteOutlined />,
                          // Greyed out with the server's own reason, so the user is
                          // not invited to click into a refusal.
                          label: item?.delete_blocked_reason ? (
                            <Tooltip title={item.delete_blocked_reason}>
                              {t("projectTypeMenu.remove")}
                            </Tooltip>
                          ) : (
                            t("projectTypeMenu.remove")
                          ),
                          disabled: !!item?.delete_blocked_reason,
                          danger: true
                        }
                      ]
                    : [])
                ],
                onClick: handleMenuClick
              }}
              trigger={["click"]}
              placement="bottomRight"
            >
              <Button variant="outlined" size="small" icon={<MoreOutlined />} />
            </Dropdown>
          )}
        </div>
      )}
    </Flex>
  )
}
